'use strict';

// external import
import React, {
    Component,
    MouseEvent
} from 'react';
import {connect} from 'react-redux';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import parse from 'html-react-parser';
import memoize from 'memoize-one';
import _ from 'lodash';

import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {Toast} from 'primereact/toast';

// shared import
import {__} from '~/shared/src/utilities/messageCatalog';
import User from '~/shared/src/model/User';
import Joi from '~/shared/src/utilities/joiExtended';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {requestBaseParametersSchema} from '~/shared/src/model/BaseRequest';
import {panelSchema} from '~/shared/src/validationSchemes/panel';
import {SaveDataResult} from '~/shared/src/utilities/modules/data';
import {
    LoadConfigResult,
    LoadDataResult,
    ClearConfigParameters,
    GetViewKeyAndRootItemIdByNameAndDisplayAttributeResult,
    RequestConfig
} from '~/shared/src/utilities/modules/form';
import {getFormTestTemplate} from '~/shared/src/utilities/testTemplateExport';
import StackError from '~/shared/src/base/StackError';
import LocalizableError from '~/shared/src/base/LocalizableError';

// frontend import
import Form, {
    Form as FormClass,
    DefaultProps as FormDefaultProps,
    defaultProps as formDefaultProps,
    ComponentProps as FormComponentProps,
    componentProps as formComponentProps,
    Props as FormProps
} from '~/frontend/src/components/base/Form';
import LoadingMaskPanel from '~/frontend/src/components/base/LoadingMaskPanel';
import Toolbar, {
    ComponentProps as ToolbarComponentProps,
    componentProps as toolbarComponentProps
} from '~/frontend/src/components/base/FormView/Toolbar';

import {
    getPanelIcon as getPanelIconOrig,
    getPanelTitle as getPanelTitleOrig,
    ItemTitleConfig,
    ItemTypeIconConfig,
    setPanelInfo as setPanelInfoOrig
} from '~/frontend/src/utilities/panelInfo';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {getPanelElusiveData} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {
    AppDispatch,
    StoreState,
    Thunk
} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';

import * as PanelManagerActions from '~/frontend/src/actions/panelManager';
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import {SetPanelPath} from '~/frontend/src/actions/panelManager/setPanelPath';
import {SetPanelDirty} from '~/frontend/src/actions/panelManager/setPanelDirty';
import {SetPanelInvalid} from '~/frontend/src/actions/panelManager/setPanelInvalid';
import {SetPanelInfo} from '~/frontend/src/actions/panelManager/setPanelInfo';
import hideConfirmCloseDialog from '~/frontend/src/actions/panelManager/hideConfirmCloseDialog';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import * as ElusiveDataActions from '~/frontend/src/actions/elusiveData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';
import {
    setElusiveData,
    SetElusiveDataAction,
    ClearElusiveDataForSubpanels
} from '~/frontend/src/actions/elusiveData';
import * as IntercomActions from '~/frontend/src/actions/intercom';
import {
    MsgParams,
    SetMessage,
    UnsetMessage
} from '~/frontend/src/actions/intercom';
import {PanelIntercomState} from '~/frontend/src/reducers/intercom';
import cancelRequests, {CancelRequests} from '~/frontend/src/actions/ajax/cancelRequests';
import messageUtils from '~/shared/src/utilities/messageUtils';
import {getStatusColorStyleString} from '~/frontend/src/utilities/renderHelpers';
import {PanelProps} from '~/frontend/src/components/base/page/PageTabPanel/definitions';


type PanelElusiveData = PanelElusiveDataState & PanelErrorData & PanelDirtyData & { showCouldNotSavePopup? : boolean };


type SetParams = ( panelKey : string, panelProps : Partial<ComponentProps> ) => SetParamsAction;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;
type SetElusiveData = ( panelKey : string, data : PanelElusiveData ) => SetElusiveDataAction;


type DefaultProps = FormDefaultProps & {
    readOnly? : boolean;
    showToolbar? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...formDefaultProps,
    readOnly   : false,
    showToolbar: true
};

type Props = FormProps & {

    /**
     * The name of the view
     */
    viewName? : string;

    /**
     * The title to display the panel with. If not set the title will be computed.
     */
    title? : string;

    /**
     * The title for the tab of the panel. If not set the title will be computed.
     */
    tabTitle? : string;

    /**
     * The title for the tab tooltip of the panel. If not set the title will be computed.
     */
    tooltipTitle? : string;

    /**
     * The icon to display the panel with. If not set the icon will be computed.
     */
    iconCls? : string;

    exportConfig? : RequestConfig;

    subPanelProps? : { [ subPanelKey : string ] : PanelProps };
};

export type ComponentProps = DefaultProps & Props;

type StateProps = {

    /**
     * The intercom messages for this component.
     *
     * Will be mapped from global state.
     */
    intercomMessages? : PanelIntercomState;

    /**
     * The elusive data (not persisted by redux store) for this component.
     *
     * Will be mapped from global state.
     */
    elusiveData? : PanelElusiveData;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

type ActionPropsRaw = {
    // panel manager actions
    closePanel : ClosePanel;
    setPanelDirty : SetPanelDirty;
    setPanelInvalid : SetPanelInvalid;
    setPanelInfo : SetPanelInfo;
    setPanelPath : SetPanelPath;

    // common panel actions
    setParams : SetParams;
    unsetParam : UnsetParam;

    // elusiveData actions
    setElusiveData : SetElusiveData;
    clearElusiveDataForSubpanels : ClearElusiveDataForSubpanels;

    // intercom actions
    setMessage : SetMessage;
    unsetMessage : UnsetMessage;

    // xhr actions
    cancelRequests : CancelRequests;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

const Actions = Object.assign( {}, PanelManagerActions, PanelActions, ElusiveDataActions, IntercomActions, { cancelRequests } ) as any;

type AllProps = Required<DefaultProps> & Props & StateProps & ActionProps;


type State = {
    editable : boolean;
    isDirty : boolean;
    errorCount : number;
    errorTooltip? : string | JSX.Element | ( string | JSX.Element )[];
    errorMessage? : string | JSX.Element | ( string | JSX.Element )[];
    showDataNoLongerValidDialog : boolean;
};


/**
 * FormView
 */
class FormView extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static panelType = 'Form' as string;
    static panelTabTitle = 'YN.base.frontend.form.tabTitle';
    static panelTabIcon = 'itemTypeIcon:CFG:Form' as string;

    static panelPropsSchemaMsgIdPrefix = 'YN.base.form';
    static panelPropsSchema = Joi.object()
        .concat( panelSchema )
        .concat( Joi.object( {
            // panelSchema defaults
            panelType    : Joi.string().valid( FormView.panelType ),
            panelTabTitle: Joi.string().default( FormView.panelTabTitle ),
            panelTabIcon : Joi.string().default( FormView.panelTabIcon )
        } ) )
        .concat( requestBaseParametersSchema )
        .concat( Joi.object()
            .keys( {
                viewKey     : Joi.string()
                    .required(),
                rootItemId  : Joi.number(),
                rootItemKey : Joi.string(),
                rootItemType: Joi.string()
            } )
            .xor( 'rootItemId', 'rootItemKey' )
            .xor( 'rootItemId', 'rootItemType' )
        )
        .concat( Joi.object( {
            showToolbar: Joi.boolean(),
            dataContext: Joi.string()
        } ) );

    /**
     * YOONITE lifecycle method
     */
    static onClosePanelAction( props : ComponentProps & { dontClearData : boolean } ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const params : ClearConfigParameters = {
                      configHash: props.configHash
                  },
                  clearConfigRecursive           = ( subPanelProps? : PanelProps ) => {
                      if ( subPanelProps ) {
                          const subPanelPropsKeys = Object.keys( subPanelProps );

                          for ( const key of subPanelPropsKeys ) {
                              if ( subPanelProps[ key ].configHash ) {
                                  void xhr.post(
                                      `${BASE_URL}/api/${key}/clearConfig`,
                                      { configHash: subPanelProps[ key ].configHash },
                                      undefined,
                                      'cleanup',
                                      'clearConfig',
                                      dispatch
                                  );
                              }

                              clearConfigRecursive( subPanelProps[ key ].subPanelProps );
                          }
                      }
                  };

            // TODO: move to form/index and call there (even from tree)

            // abort running request
            dispatch( cancelRequests( props.uid ) );

            // clear global data
            if ( !props.dontClearData ) {
                delete global.data[ props.dataContext ];
            }

            // clear backend data
            void xhr.post(
                `${BASE_URL}/api/form/clearConfig`,
                params,
                undefined,
                'cleanup',
                'clearConfig',
                dispatch
            );
            clearConfigRecursive( props.subPanelProps );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSavePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await FormView.savePanelData(
                dispatch,
                getState,
                props
            );

            if ( result !== false && result.success ) {
                return true;
            }
            else {
                return false;
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static onSaveAndClosePanelAction( props : ComponentProps ) {
        return async ( dispatch : AppDispatch, getState : () => StoreState ) => {
            const result = await FormView.savePanelData(
                dispatch,
                getState,
                props,
                [
                    props.uid,
                    'notification',
                    'saveSuccess',
                    'closePanel'
                ]
            );

            // no save error -> close panel
            if ( result !== false && result.success ) {
                await dispatch( closePanel( props.uid ) );
            }
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static async savePanelData( dispatch : AppDispatch, getState : () => StoreState, props : ComponentProps, successMsgParams : MsgParams | undefined = undefined ) : Promise<SaveDataResult | false> {
        const propsWithDefaults : DefaultProps & Props = {
                  ...defaultProps,
                  ...props
              },
              {
                  uid
              }                                        = propsWithDefaults,
              elusiveData                              = getPanelElusiveData( uid, getState() ),
              errorData                                = elusiveData?.errorData as PanelErrorData[ 'errorData' ];

        // panel has errors -> show popup
        if ( errorData?.count ) {
            dispatch( hideConfirmCloseDialog( uid ) );
            dispatch( setElusiveData( uid, { showCouldNotSavePopup: true } ) );

            return Promise.resolve( false );
        }

        // panel has no errors -> save
        else {
            return Form.doSaveData( uid, uid, successMsgParams );
        }
    }

    /**
     * YOONITE lifecycle method
     */
    static buildPanelPathFromProps( props : Pick<AllProps, 'dataContext' | 'rootItemId' | 'viewName' | 'viewKey' | 'schema'> ) : string | undefined {
        const item = global.data[ props.dataContext ]?.items?.[ props.rootItemId ?? 0 ];

        let retVal : string | undefined;

        if ( item ) {
            retVal = `#${FormView.panelType}/${props.viewName ?? props.viewKey}/${props.schema}/${encodeURIComponent( item.displayAttributeValue )}`;
        }

        return retVal;
    }

    /**
     * YOONITE lifecycle method
     */
    static async buildPanelPropsFromPath( path : string, historyNavigation : boolean ) : Promise<Partial<ComponentProps>> {
        const retVal : Partial<ComponentProps> = {},
              parts                            = path.match( /#([^?]*)(\?(.*))?/ ),
              params                           = parts?.[ 1 ]?.split( '/' ) ?? [],
              stackError                       = new StackError( 'YN.base.resolvePanelPropsFromURL.error.title', { path: path } );

        let viewName : string | undefined;

        // params[ 0 ] = panelType

        // params[ 1 ] = viewName
        if ( params?.[ 1 ] ) {
            viewName = params[ 1 ];
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.form.resolvePanelPropsFromURL.error.couldNotResolveViewName' ) );
        }

        // params[ 2 ] = schema
        if ( params?.[ 2 ] ) {
            retVal.schema = params[ 2 ] as Schema;
        }
        else {
            stackError.add( new LocalizableError( 'YN.base.form.resolvePanelPropsFromURL.error.couldNotResolveSchema' ) );
        }

        // params[ 3 ] = rootItem.displayAttributeValue
        if ( params?.[ 3 ] ) {
            if ( viewName && retVal.schema ) {
                const displayAttributeValue = decodeURIComponent( params[ 3 ] );

                // resolve viewKey and rootItemId
                const res    = await xhr.post(
                          `${BASE_URL}/api/form/getViewKeyAndRootItemIdByNameAndDisplayAttribute`,
                          {
                              schema: retVal.schema,
                              viewName,
                              displayAttributeValue
                          }
                      ),
                      result = res?.data as GetViewKeyAndRootItemIdByNameAndDisplayAttributeResult;

                if ( result ) {
                    if ( result.data?.viewKey ) {
                        retVal.viewKey = result.data.viewKey;
                    }
                    else if ( !historyNavigation ) {
                        stackError.add( new LocalizableError( 'YN.base.form.resolvePanelPropsFromURL.error.couldNotResolveViewKey', { name: viewName } ) );
                    }

                    if ( result.data?.rootItemId ) {
                        retVal.rootItemId = result.data.rootItemId;
                    }
                    else if ( !historyNavigation ) {
                        stackError.add( new LocalizableError( 'YN.base.form.resolvePanelPropsFromURL.error.couldNotResolveRootItemId', { attribute: displayAttributeValue } ) );
                    }

                    if ( result.data?.status ) {
                        retVal.status = result.data.status as Status;
                    }
                }
            }
        }
        else if ( !historyNavigation ) {
            stackError.add( new LocalizableError( 'YN.base.form.resolvePanelPropsFromURL.error.couldNotResolveRootItem' ) );
        }

        if ( stackError.hasErrors() ) {
            throw ( stackError.toString() );
        }

        return retVal;
    }

    /* properties *************************************************************************************************************************/

    toast : Toast | null = null;
    form : FormClass | null = null;

    getPanelIcon = getPanelIconOrig;
    getPanelTitle = getPanelTitleOrig;
    setPanelInfo = setPanelInfoOrig;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            editable                   : true,
            isDirty                    : false,
            errorCount                 : 0,
            showDataNoLongerValidDialog: false
        };

        this.checkForIntercomMessages = memoize( this.checkForIntercomMessages );
        this.getPanelIcon = memoize( this.getPanelIcon );
        this.getPanelTitle = memoize( this.getPanelTitle );
        this.setPanelInfo = memoize( this.setPanelInfo );

        this.buildPanelPathFromProps = this.buildPanelPathFromProps.bind( this );
        this.onFormConfigLoad = this.onFormConfigLoad.bind( this );
        this.onFormDataInvalid = this.onFormDataInvalid.bind( this );
        this.onFormDataLoad = this.onFormDataLoad.bind( this );
        this.toolbarDoLoadData = this.toolbarDoLoadData.bind( this );
        this.onDownloadTestTemplateButtonClick = this.onDownloadTestTemplateButtonClick.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  elusiveData
              }                       = props,
              retVal : Partial<State> = {

                  // editable
                  editable: true,

                  // is dirty
                  isDirty: elusiveData?.isDirty ?? false,

                  // error
                  errorCount  : elusiveData?.errorData?.count ?? 0,
                  errorTooltip: undefined,
                  errorMessage: undefined
              };

        // errors
        if ( elusiveData?.errorData ) {
            const errorData = elusiveData.errorData;

            retVal.errorTooltip = <div key="error" className="yn-tooltip-error">
                <div className="yn-tooltip-header">
                    {__( 'YN.base.common.tooltip.error.title' )}
                </div>
                {errorData.tooltip}
            </div>;

            retVal.errorMessage = <>
                <div className="yn-error-message">
                    {__( 'YN.base.form.couldNotSaveWhileErrors.text' )}
                </div>
                <hr className="yn-error-ruler"/>
                <div className="yn-error-suberror">
                    {errorData.message}
                </div>
            </>;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.afterRender();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : ComponentProps ) {
        this.afterRender();
    }

    /**
     * REDUX stuff
     */
    shouldComponentUpdate( nextProps : AllProps, nextState : State ) : boolean {
        const changedProps = objectUtils.getDiffKeys(
                  this.props,
                  nextProps,
                  [
                      'dataDescriptors',
                      'dataStructure',
                      'elusiveData',
                      'intercomMessages',
                      'layoutElements',
                      'layoutStructure',
                      'user',
                      'xhrRequestsForComponent'
                  ]
              ),
              changedState = objectUtils.getDiffKeys( this.state, nextState );

        let retVal : boolean = Boolean( changedProps.length ) || changedState.includes( 'editable' );

        if ( changedState.includes( 'isDirty' ) ) {
            this.props.setPanelDirty( this.props.uid, nextState.isDirty );

            retVal = true;
        }

        if ( changedState.includes( 'errorCount' ) ) {
            this.props.setPanelInvalid( this.props.uid, !!nextState.errorCount );

            retVal = true;
        }

        if ( changedState.includes( 'showDataNoLongerValidDialog' ) ) {
            retVal = true;
        }

        return retVal;
    }


    /* event handlers *********************************************************************************************************************/

    onFormConfigLoad( config : LoadConfigResult, reloadConfig : boolean = false ) {
        this.props.setParams( this.props.uid, {
            viewName    : config.data.viewName,
            title       : config.data.title,
            tabTitle    : config.data.tabTitle,
            tooltipTitle: config.data.tooltipTitle,
            iconCls     : config.data.iconCls,
            showToolbar : config.data.showToolbar,
            exportConfig: config.data.exportConfig
        } );

        if ( reloadConfig ) {
            this.props.clearElusiveDataForSubpanels( this.props.uid );
            this.props.unsetParam( this.props.uid, 'subPanelProps' );
            this.props.unsetParam( this.props.uid, 'persistentSubPanelProps' );
        }
    }

    onFormDataLoad( config : LoadDataResult ) {
        window.setTimeout(
            () => {
                const path = this.buildPanelPathFromProps( this.props.dataContext, this.props.schema, this.props.viewKey, this.props.viewName ?? ( config as unknown as LoadConfigResult ).data.viewName, this.props.rootItemId );

                if ( path ) {
                    this.props.setPanelPath( this.props.uid, path );
                }
            },
            15
        );
    }

    onFormDataInvalid() {
        this.setState( { showDataNoLongerValidDialog: true } );
    }

    toolbarDoLoadData( reloadConfig : boolean = false ) {
        void this.form?.loadData( reloadConfig );
    }

    onDownloadTestTemplateButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        const { schema, elusiveData } = this.props;

        if ( elusiveData?.layoutElementMappings?.cache ) {
            fileDownload( getFormTestTemplate( schema, elusiveData.layoutElementMappings.cache, this.form?.props?.layoutStructure ), 'formTest', 'spec.ts' );
        }
    }

    /* private ****************************************************************************************************************************/

    buildPanelPathFromProps( dataContext : AllProps['dataContext'], schema : AllProps['schema'], viewKey : AllProps['viewKey'], viewName : AllProps['viewName'], rootItemId : AllProps['rootItemId'] ) {
        return FormView.buildPanelPathFromProps( { dataContext, schema, viewKey, viewName, rootItemId } )
    }

    afterRender() {
        const {
                  uid,
                  dataContext,
                  schema,
                  viewKey,
                  viewName,
                  rootItemId,
                  rootItemType,
                  iconCls,
                  tabTitle    : tabTitleOrig,
                  tooltipTitle: tooltipTitleOrig,
                  intercomMessages
              }                                                   = this.props,
              {
                  isDirty,
                  errorCount
              }                                                   = this.state,
              itemTypeIconConfig : ItemTypeIconConfig | undefined = rootItemId
                  ? {
                      type: 'byItemId',
                      dataContext,
                      id  : rootItemId
                  }
                  : rootItemType
                      ? {
                          type    : 'byItemType',
                          schema,
                          itemType: rootItemType
                      }
                      : undefined,
              icon                                                = this.getPanelIcon( FormView.panelTabIcon, iconCls, itemTypeIconConfig ),
              itemTitleConfig : ItemTitleConfig | undefined       = rootItemId
                  ? {
                      dataContext,
                      id: rootItemId
                  }
                  : undefined,
              tabTitle                                            = this.resolveTitle( tabTitleOrig ),
              title                                               = this.getPanelTitle( FormView.panelTabTitle, tabTitle, itemTitleConfig ),
              tooltipTitle                                        = this.resolveTitle( tooltipTitleOrig ),
              path                                                = this.buildPanelPathFromProps( dataContext, schema, viewKey, viewName, rootItemId );

        this.checkForIntercomMessages( intercomMessages );

        this.setPanelInfo( uid, icon, title, tooltipTitle, isDirty, !!errorCount, path );
    }

    /**
     * Checks if there are intercom messages present and shows them if.
     *
     * @private
     */
    checkForIntercomMessages( intercomMessages? : PanelIntercomState ) {
        // check for toasts
        if ( this.toast && intercomMessages?.toast ) {
            const toastKeys = Object.keys( intercomMessages.toast );

            for ( const key of toastKeys ) {
                // show toast
                if ( key === 'configChanged' && intercomMessages.toast[ key ].id === 'showWarn' ) {
                    this.toast.show( {
                        severity: 'warn',
                        //sticky  : true,
                        summary: __( 'YN.base.form.configChanged.title' ),
                        detail : __( 'YN.base.form.configChanged.text' )
                    } );
                }

                else if ( intercomMessages.toast[ key ] ) {
                    this.toast.show( intercomMessages.toast[ key ] );
                }

                // clear toast message
                this.props.unsetMessage( this.props.uid, 'toast', key as string );
            }
        }
    }

    resolveTitle( title? : string ) {
        const {
                  dataContext,
                  schema,
                  viewKey,
                  viewName,
                  rootItemId: rootItemIdOrig,
                  elusiveData
              }          = this.props,
              rootItemId = rootItemIdOrig ?? elusiveData?.data?.rootItemId;

        let retVal = title;

        if ( retVal ) {
            const rootItem = global.data[ dataContext ]?.items?.[ rootItemId ];

            if ( rootItem ) {
                const labelTemplateResolver = messageUtils.getMessageTemplateStringResolver( rootItem ),
                      itemStatusStyle       = getStatusColorStyleString( rootItem.statusId, schema );

                retVal = labelTemplateResolver( __( retVal, { viewKey, viewName: viewName ?? '', itemStatusStyle } ) );
            }
        }

        return retVal;
    }

    render() {
        const {
                  uid,
                  exportConfig,
                  elusiveData,
                  user,
                  showToolbar,
                  mode,
                  viewKey
              }            = this.props,
              {
                  errorCount,
                  errorTooltip,
                  errorMessage,
                  isDirty,
                  showDataNoLongerValidDialog
              }            = this.state,
              toolbarProps = {
                  ..._.pick( this.props, toolbarComponentProps ) as ToolbarComponentProps,

                  key: `${uid}_toolbar`,

                  doLoadData: this.toolbarDoLoadData,

                  onDownloadTestTemplateButtonClick: this.onDownloadTestTemplateButtonClick,

                  saveButtonErrorCount          : errorCount,
                  saveButtonErrorTooltip        : errorTooltip,
                  xmlExportConfig               : exportConfig,
                  showCopyXmlButton             : !!exportConfig?.requestModule,
                  showDownloadXmlButton         : !!exportConfig?.requestModule,
                  showDownloadTestTemplateButton: !!config.sysGroups?.admin && user?.isMemberOfGroup( config.sysGroups.admin ),
                  showModifiedHint              : isDirty,

                  viewKey
              },
              formProps    = {
                  ..._.pick( this.props, formComponentProps ) as FormComponentProps,

                  key: `${uid}_form`,

                  onConfigLoad : this.onFormConfigLoad,
                  onDataLoad   : this.onFormDataLoad,
                  onInvalidData: this.onFormDataInvalid
              };

        return <>
            <Dialog
                key={`${uid}_couldNotSaveWhileErrors_dialog`}
                header={__( 'YN.base.form.couldNotSaveWhileErrors.title' )}
                footer={<div><Button label="OK" className="p-button-outlined"
                                     onClick={() => this.props.setElusiveData( this.props.uid, { showCouldNotSavePopup: false } )}/></div>}
                content=""
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                position="top"
                visible={elusiveData?.showCouldNotSavePopup}
                style={{ width: 'auto', maxWidth: '80%' }}
                closable={false}
                className="yn-error-dialog"
                onHide={() => {
                }}>
                {errorMessage}
            </Dialog>
            <Dialog
                key={`${uid}_dataNotValid_dialog`}
                header={__( 'YN.base.form.dataNotValid.title' )}
                footer={<div>
                    <Button
                        key="okButton"
                        label={__( 'YN.base.common.ok' )}
                        className="p-button p-button-outlined"
                        onClick={() => {
                            void this.props.closePanel( uid )
                        }}
                    />
                </div>}
                content=""
                position="top"
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                visible={showDataNoLongerValidDialog}
                closable={false}
                className="yn-warning-dialog"
                onHide={() => {
                    void this.props.closePanel( uid )
                }}
                style={{ width: 'auto', maxWidth: '80%' }}
            >
                <p>{parse( __( 'YN.base.form.dataNotValid.text' ) )}</p>
            </Dialog>

            {showToolbar &&
                <Toolbar
                    {...toolbarProps}
                />
            }

            <LoadingMaskPanel key={`${uid}_loadingMask`} forComponent={uid} className="yn-flex-layout-parent">
                <Toast
                    ref={( el ) => this.toast = el}
                    content=""

                    key={`${uid}_toast`}

                    position="bottom-right"
                    style={{ position: 'absolute' }}
                />
                {mode !== 'standalonePopup' &&
                    <div key={`${uid}_formview`} className="yn-content-panel-container yn-formview-panel" style={{ overflowX: 'hidden' }}>
                        <Form
                            ref={( el ) => this.form = el}

                            {...formProps}
                        />
                    </div>
                }
                {mode === 'standalonePopup' &&
                    <Form
                        ref={( el ) => this.form = el}

                        {...formProps}
                    />
                }
            </LoadingMaskPanel>
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        intercomMessages: undefined,
        elusiveData     : undefined,
        user            : undefined
    };

    // get intercom messages
    if ( state.intercom?.[ ownProps.uid ] ) {
        retVal.intercomMessages = state.intercom[ ownProps.uid ];
    }

    // get elusiveData
    // for the data (items and references) belonging to this component
    if ( state.elusiveData?.[ ownProps.dataContext ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.dataContext ]
        };
    }
    // for all other information belonging to this component
    if ( state.elusiveData?.[ ownProps.uid ] ) {
        retVal.elusiveData = {
            ...retVal.elusiveData ?? {},
            ...state.elusiveData[ ownProps.uid ]
        };
    }

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( FormView );
